
/**
 * The Job Search Pack's price for this visitor: less what they already paid
 * for one posting (a tailored PDF, an interview prep), as the checkout will
 * charge it (server/utils/packCredit.ts). Shared, so every offer on the page
 * shows the same price; the full price until `load` has answered. In the
 * visitor's currency (usePricing).
 */
export function usePackCredit() {
  const state = useState<{ credit: number, price: number, currency?: string } | null>('pack-credit', () => null)
  const { currency, pricing } = usePricing()
  const { t, locale } = useI18n()

  async function load(generationId?: string | null) {
    try {
      state.value = await $fetch<{ credit: number, price: number, currency?: string }>('/api/checkout/pack-credit', { query: { generationId: generationId ?? undefined, locale: locale.value } })
    } catch {
      // The full price stays on screen; the checkout still applies any credit.
    }
  }

  // An answer for another currency (the language was switched since) is not used.
  const current = computed(() => (state.value && (state.value.currency ?? 'USD') === currency.value ? state.value : null))
  const credit = computed(() => current.value?.credit ?? 0)
  const full = computed(() => pricing.value.prices.jobPack)
  const format = (amount: number) => pricing.value.format(amount)
  return {
    load,
    credit,
    /** "$19.10", or the full "$29". */
    packPrice: computed(() => format(current.value?.price ?? full.value)),
    /** "$9.90" taken off, or null. */
    creditText: computed(() => (credit.value > 0 ? format(credit.value) : null)),
    /** Why the pack costs less here: "$29 minus the $9.90 you already paid…", or null. */
    creditNote: computed(() => (credit.value > 0 && (current.value?.price ?? full.value) < full.value
      ? t('premium.jobPack.creditExplain', { full: format(full.value), credit: format(credit.value) })
      : null)),
  }
}
